// src/pages/Dashboard/components/Dialogs/GenerateDocumentDialog.js
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * Dialog para gerar documentos jurídicos
 * ✅ MODIFICADO: Suporta tipo "Outros" com nome customizado
 * @param {boolean} isOpen - Se o dialog está aberto
 * @param {object} documento - Documento selecionado
 * @param {string} instrucoesAdicionais - Instruções adicionais
 * @param {string} conteudoGerado - Conteúdo gerado
 * @param {boolean} conteudoDisponivel - Se conteúdo está disponível
 * @param {boolean} gerando - Se está gerando
 * @param {boolean} baixando - Se está baixando
 * @param {function} onClose - Callback ao fechar
 * @param {function} onUpdateInstrucoes - Callback ao atualizar instruções
 * @param {function} onGenerate - Callback para gerar conteúdo (aceita instruções e nome como parâmetros)
 * @param {function} onCopy - Callback para copiar conteúdo
 * @param {function} onDownload - Callback para baixar DOCX (aceita nome customizado)
 */
const GenerateDocumentDialog = ({
  isOpen,
  documento,
  instrucoesAdicionais,
  conteudoGerado,
  conteudoDisponivel,
  gerando,
  baixando,
  onClose,
  onUpdateInstrucoes,
  onGenerate,
  onCopy,
  onDownload
}) => {
  const [localInstrucoes, setLocalInstrucoes] = useState('');
  const [nomeDocumentoCustomizado, setNomeDocumentoCustomizado] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalInstrucoes(instrucoesAdicionais);
      setNomeDocumentoCustomizado('');
    }
  }, [isOpen, instrucoesAdicionais]);

  if (!isOpen || !documento) return null;

  // ✅ NOVO: Detecta se o tipo é "Outros"
  const isOutros = documento.id === 'outros' || documento.titulo.toLowerCase().includes('outros');

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // ✅ MODIFICADO: Passa parâmetros diretamente para evitar race condition
  const handleGenerate = async () => {
    // Validação: Se for "Outros", nome é obrigatório
    if (isOutros && !nomeDocumentoCustomizado.trim()) {
      alert('Por favor, informe o nome do documento');
      return;
    }

    // Atualiza estado para histórico, mas não depende dele para geração
    onUpdateInstrucoes(localInstrucoes);
    
    try {
      // ✅ CORRIGIDO: Passa instruções e nome diretamente como parâmetros
      const nomeDocumento = isOutros ? nomeDocumentoCustomizado.trim() : documento.titulo;
      await onGenerate(localInstrucoes, nomeDocumento);
    } catch (error) {
      alert(error.message || 'Erro ao gerar documento');
    }
  };

  const handleCopy = async () => {
    try {
      await onCopy();
      alert('Conteúdo copiado para a área de transferência!');
    } catch (error) {
      alert(error.message || 'Erro ao copiar conteúdo');
    }
  };

  // ✅ MODIFICADO: Passa nome customizado para download
  const handleDownload = async () => {
    try {
      const nomeDocumento = isOutros ? nomeDocumentoCustomizado.trim() : documento.titulo;
      await onDownload(nomeDocumento);
    } catch (error) {
      alert(error.message || 'Erro ao baixar documento');
    }
  };

  return (
    <div className="dialog-overlay-dashboard" onClick={handleOverlayClick}>
      <div className="dialog-content-dashboard dialog-large" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header-dashboard">
          <h3>Gerar Documento</h3>
          <button className="dialog-close-dashboard" onClick={onClose}>×</button>
        </div>
        
        <div className="dialog-body-dashboard">
          <p><strong>Tipo: {documento.titulo}</strong></p>
          
          {/* ✅ NOVO: Campo condicional para nome customizado quando tipo for "Outros" */}
          {isOutros && (
            <div className="form-group-dashboard">
              <label>Nome do documento: *</label>
              <input
                type="text"
                value={nomeDocumentoCustomizado}
                onChange={(e) => setNomeDocumentoCustomizado(e.target.value)}
                placeholder="Ex: Recurso de Agravo, Ação Executiva (Requerimento Executivo), Procedimento Cautelar..."
                disabled={gerando}
                required
              />
            </div>
          )}
          
          <div className="form-group-dashboard">
            <label>Instruções adicionais (opcional):</label>
            <textarea
              value={localInstrucoes}
              onChange={(e) => setLocalInstrucoes(e.target.value)}
              placeholder="Digite instruções específicas..."
              rows={3}
              disabled={gerando}
            />
          </div>
          
          {conteudoDisponivel && (
            <div className="conteudo-gerado-dashboard">
              <label>Conteúdo gerado:</label>
              <div className="conteudo-acoes-dashboard">
                <button 
                  onClick={handleCopy}
                  disabled={baixando}
                >
                  📋 Copiar
                </button>
                <button 
                  onClick={handleDownload}
                  disabled={baixando}
                >
                  {baixando ? (
                    <>
                      <span className="spinner-small"></span>
                      Gerando...
                    </>
                  ) : (
                    '📄 Download .DOCX'
                  )}
                </button>
              </div>
              <div className="markdown-preview-dashboard">
                <ReactMarkdown>{conteudoGerado}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
        
        <div className="dialog-footer-dashboard">
          <button 
            onClick={onClose}
            disabled={gerando || baixando}
          >
            Fechar
          </button>
          {!conteudoDisponivel && (
            <button 
              onClick={handleGenerate} 
              disabled={gerando || (isOutros && !nomeDocumentoCustomizado.trim())}
            >
              {gerando ? (
                <>
                  <span className="spinner-small"></span>
                  Gerando conteúdo...
                </>
              ) : (
                'Gerar Conteúdo'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateDocumentDialog;